import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AddCustomFieldsComponent } from '../add-custom-fields/add-custom-fields.component';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { DeleteCustomField, FetchAllCustomFields, FetchCustomFieldsPage } from '../../store/settings.actions';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ICustomField } from '../../models/settings.models';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/config/language.service';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { IPagination, IPageCount } from 'src/app/shared/models/page.model';

@Component({
  selector: 'app-list-custom-fields',
  templateUrl: './list-custom-fields.component.html',
  styleUrls: ['./list-custom-fields.component.scss']
})
export class ListCustomFieldsComponent implements OnInit, OnDestroy {
  customFields$: Observable<ICustomField[]> = this.store.select((state) => state.settings.customFields);
  customFieldsMetadata$: Observable<IPagination | null> = this.store.select((state) => state.settings.customFieldsMetadata);
  isLoading$: Observable<boolean> = this.store.select((state) => state.settings.loading);
  currentLanguage: string = this.languageService.getCurrentLanguage();
  private destroy$ = new Subject<void>();
  private meta: IPagination | null = null;
  private isLoading: boolean = false;
  constructor(
    private dialog: MatDialog,
    private store: Store<AppState>,
    private translateService: TranslateService,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.store.dispatch(FetchCustomFieldsPage({ page: { number: 1, size: DEFAULT_PAGE_SIZE }, append: false }));

    this.customFieldsMetadata$
      .pipe(takeUntil(this.destroy$))
      .subscribe((m) => (this.meta = m));
    this.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => (this.isLoading = v));
  }

  onScroll(e: Event): void {
    const el = e.target as HTMLElement;
    const threshold = 120; // px before bottom to prefetch
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (!nearBottom) return;

    if (this.isLoading) return;
    if (!this.meta) return;
    if (this.meta.page >= this.meta.pages) return;

    const nextPage: IPageCount = { number: this.meta.page + 1, size: this.meta.limit || DEFAULT_PAGE_SIZE };
    this.store.dispatch(FetchCustomFieldsPage({ page: nextPage, append: true }));
  }

  getLabel(def: ICustomField): string {
    return (
      def?.label?.[this.currentLanguage] ||
        // or first language that has value
      Object.values(def?.label ?? {}).find((value) => value !== '') ||
      def?.technical_name ||
      ''
    );
  }

  getVisibilityLabel(visibility: string | string[] | undefined): string {
    if (!visibility) return '';
    const values = Array.isArray(visibility) ? visibility : [visibility];
    return values
      .map((v) => this.translateService.instant('custom_fields.visibility_options.' + v))
      .filter(Boolean)
      .join(', ');
  }

  addCustomField(customField?: ICustomField) {
    this.dialog.open(AddCustomFieldsComponent, {
      data: {
        customField: customField
      }
    });
  }


  deleteCustomField(id: number) {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.store.dispatch(DeleteCustomField({ id }));
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
